import { TierLabel, PriceLookupKey, BillingInterval, TierVersion, PaymentServiceLabel, SubscriptionStatus, } from "@memento/types/subscription";
const PriceLookupKeyToTierLabel = {
    [PriceLookupKey.BUSINESS_MONTHLY]: TierLabel.BUSINESS,
    [PriceLookupKey.BUSINESS_YEARLY]: TierLabel.BUSINESS,
    [PriceLookupKey.BUSINESS_MONTHLY_AS_YEARLY]: TierLabel.BUSINESS,
    [PriceLookupKey.PLATINUM_MONTHLY]: TierLabel.PLATINUM,
    [PriceLookupKey.PLATINUM_YEARLY]: TierLabel.PLATINUM,
    [PriceLookupKey.PLATINUM_MONTHLY_AS_YEARLY]: TierLabel.PLATINUM,
    [PriceLookupKey.PREMIUM_MONTHLY]: TierLabel.PREMIUM,
    [PriceLookupKey.PREMIUM_YEARLY]: TierLabel.PREMIUM,
    [PriceLookupKey.PREMIUM_MONTHLY_AS_YEARLY]: TierLabel.PREMIUM,
    [PriceLookupKey.PLUS_MONTHLY]: TierLabel.PLUS,
    [PriceLookupKey.PLUS_YEARLY]: TierLabel.PLUS,
    [PriceLookupKey.PLUS_MONTHLY_AS_YEARLY]: TierLabel.PLUS,
    [PriceLookupKey.PREFERRED_MONTHLY]: TierLabel.PREFERRED,
    [PriceLookupKey.PREFERRED_YEARLY]: TierLabel.PREFERRED,
    [PriceLookupKey.PREFERRED_MONTHLY_AS_YEARLY]: TierLabel.PREFERRED,
    [PriceLookupKey.ADVANCED_MONTHLY]: TierLabel.ADVANCED,
    [PriceLookupKey.ADVANCED_YEARLY]: TierLabel.ADVANCED,
    [PriceLookupKey.ADVANCED_MONTHLY_AS_YEARLY]: TierLabel.ADVANCED,
    [PriceLookupKey.EXTRA_MONTHLY]: TierLabel.EXTRA,
    [PriceLookupKey.EXTRA_YEARLY]: TierLabel.EXTRA,
    [PriceLookupKey.EXTRA_MONTHLY_AS_YEARLY]: TierLabel.EXTRA,
};
export const tierLabelToYearlyPriceLookupKeyMap = {
    [TierLabel.NONE]: PriceLookupKey.PLUS_YEARLY, // there is no 'NONE' equivalent for price lookup key
    [TierLabel.EXTRA]: PriceLookupKey.EXTRA_YEARLY,
    [TierLabel.ADVANCED]: PriceLookupKey.ADVANCED_YEARLY,
    [TierLabel.PREFERRED]: PriceLookupKey.PREFERRED_YEARLY,
    [TierLabel.PLUS]: PriceLookupKey.PLUS_YEARLY,
    [TierLabel.PREMIUM]: PriceLookupKey.PREMIUM_YEARLY,
    [TierLabel.PLATINUM]: PriceLookupKey.PLATINUM_YEARLY,
    [TierLabel.BUSINESS]: PriceLookupKey.BUSINESS_YEARLY,
};
const priceLookupKeyToBillingInterval = {
    [PriceLookupKey.BUSINESS_MONTHLY]: BillingInterval.MONTH,
    [PriceLookupKey.BUSINESS_YEARLY]: BillingInterval.YEAR,
    [PriceLookupKey.BUSINESS_MONTHLY_AS_YEARLY]: BillingInterval.MONTH,
    [PriceLookupKey.PLATINUM_MONTHLY]: BillingInterval.MONTH,
    [PriceLookupKey.PLATINUM_YEARLY]: BillingInterval.YEAR,
    [PriceLookupKey.PLATINUM_MONTHLY_AS_YEARLY]: BillingInterval.MONTH,
    [PriceLookupKey.PREMIUM_MONTHLY]: BillingInterval.MONTH,
    [PriceLookupKey.PREMIUM_YEARLY]: BillingInterval.YEAR,
    [PriceLookupKey.PREMIUM_MONTHLY_AS_YEARLY]: BillingInterval.MONTH,
    [PriceLookupKey.PLUS_MONTHLY]: BillingInterval.MONTH,
    [PriceLookupKey.PLUS_YEARLY]: BillingInterval.YEAR,
    [PriceLookupKey.PLUS_MONTHLY_AS_YEARLY]: BillingInterval.MONTH,
    [PriceLookupKey.PREFERRED_MONTHLY]: BillingInterval.MONTH,
    [PriceLookupKey.PREFERRED_YEARLY]: BillingInterval.YEAR,
    [PriceLookupKey.PREFERRED_MONTHLY_AS_YEARLY]: BillingInterval.MONTH,
    [PriceLookupKey.ADVANCED_MONTHLY]: BillingInterval.MONTH,
    [PriceLookupKey.ADVANCED_YEARLY]: BillingInterval.YEAR,
    [PriceLookupKey.ADVANCED_MONTHLY_AS_YEARLY]: BillingInterval.MONTH,
    [PriceLookupKey.EXTRA_MONTHLY]: BillingInterval.MONTH,
    [PriceLookupKey.EXTRA_YEARLY]: BillingInterval.YEAR,
    [PriceLookupKey.EXTRA_MONTHLY_AS_YEARLY]: BillingInterval.MONTH,
};
// Maps TierLabel to a numeric level for comparison
// NOTE: This should always be ordered from "lowest to highest" tier.
const tierLabelToNumericLevel = {
    [TierLabel.NONE]: 0,
    [TierLabel.EXTRA]: 1,
    [TierLabel.ADVANCED]: 2,
    [TierLabel.PREFERRED]: 3,
    [TierLabel.PLUS]: 4,
    [TierLabel.PREMIUM]: 5,
    [TierLabel.PLATINUM]: 6,
    [TierLabel.BUSINESS]: 7,
};
/**
 * Compare two subscriptions and return the highest tier
 * @param tierA the first subscription tier
 * @param tierB the second subscription tier
 * @returns the highest Tier between the two subscriptions
 */
export function getHigherTier(tierA, tierB) {
    const tierLevelA = tierLabelToNumericLevel[tierA.label];
    const tierLevelB = tierLabelToNumericLevel[tierB.label];
    // Choose either the highest tier level or, if those are equal, the latest
    // tier version.
    if (tierLevelA > tierLevelB ||
        (tierLevelA === tierLevelB && tierA.version >= tierB.version)) {
        return tierA;
    }
    else {
        return tierB;
    }
}
// Compares two tiers and returns:
// - a negative number if a < b
// - a positive number if a > b
// - 0 if a == b
export function compareTiers(a, b) {
    const tierLevelA = tierLabelToNumericLevel[a];
    const tierLevelB = tierLabelToNumericLevel[b];
    return tierLevelA - tierLevelB;
}
export function getOrderedTierLabels() {
    return Object.keys(tierLabelToNumericLevel);
}
// Do not return a TierLabel.NONE response from this function. This can be used
// to set the tier in the subscription object, which should only have a non-NONE
// value.
export function getTierLabelFromPriceLookupKey(lookupKey) {
    return PriceLookupKeyToTierLabel[lookupKey];
}
export function getBillingIntervalFromPriceLookupKey(lookupKey) {
    return priceLookupKeyToBillingInterval[lookupKey];
}
export function buildNoneSubscription(username) {
    return {
        username: username || null,
        createdAt: null,
        updatedAt: null,
        paymentService: null,
        paymentServiceSubscriptionId: null,
        paymentServiceCustomerId: null,
        tier: { label: TierLabel.NONE, version: TierVersion.V1 },
        isActive: false,
        status: null,
        billingInterval: null,
        price: null,
        priceDiscounted: null,
        nextPaymentDueStamp: null,
        willAutoRenew: false,
        discountPercentage: null,
        isDiscounted: false,
    };
}
const tierLabelToHumanMap = {
    [TierLabel.NONE]: "None",
    [TierLabel.EXTRA]: "Extra",
    [TierLabel.ADVANCED]: "Advanced",
    [TierLabel.PREFERRED]: "Preferred",
    [TierLabel.PLUS]: "Plus",
    [TierLabel.PREMIUM]: "Premium",
    [TierLabel.PLATINUM]: "Platinum",
    [TierLabel.BUSINESS]: "Business",
};
export function tierLabelToHuman(tierLabel) {
    return tierLabelToHumanMap[tierLabel];
}
export function normalizeSubscriptionTier(tier, upgradedBySupportTier) {
    // if the current tier is NONE, then don't use the upgradedBySupportTier
    if (tier.label === TierLabel.NONE) {
        return tier;
    }
    return upgradedBySupportTier
        ? getHigherTier(tier, upgradedBySupportTier)
        : tier;
}
const billingIntervalToHumanMap = {
    [BillingInterval.MONTH]: {
        short: "mo",
        period: "Monthly",
        long: "month",
    },
    [BillingInterval.YEAR]: {
        short: "yr",
        period: "Annually",
        long: "year",
    },
};
export function billingIntervalToHuman(billingInterval, style = "long") {
    return billingIntervalToHumanMap[billingInterval][style];
}
export const paymentServiceLabelToHuman = {
    [PaymentServiceLabel.STRIPE]: "Stripe",
    [PaymentServiceLabel.MEMENTO]: "Memento",
};
// These subscription statuses are considered terminal.
// Stripe will not allow us to update these subscription (e.g. change metadata, username)
const terminalSubscriptionStatuses = [
    SubscriptionStatus.CANCELED,
    SubscriptionStatus.INCOMPLETE_EXPIRED,
];
export const isTerminalSubscriptionStatus = (status) => {
    return terminalSubscriptionStatuses.includes(status);
};
export const sortSubscriptionsByLastUpdate = (subscriptions) => {
    const sortedSubscriptions = subscriptions.sort((a, b) => {
        return (b.paymentServiceLastUpdateEventCreatedAt -
            a.paymentServiceLastUpdateEventCreatedAt);
    });
    return sortedSubscriptions;
};
